// src/components/ProfessionalLocationsConfig.tsx
// Configuración de sedes/consultorios - Multi-sede para profesionales

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { apiService, Location } from '../services/api';
import {
  Plus,
  ArrowLeft,
  Pencil,
  Trash2,
  MapPin,
  Phone,
  Building2,
  X,
  Loader2,
  Save,
  CheckCircle,
  AlertTriangle,
  Star,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

interface User {
  id: number;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'salud' | 'belleza';
  especialidad: string;
}

interface LocationFormData {
  id?: number;
  name: string;
  address: string;
  city: string;
  phone: string;
  is_active: boolean;
}

const ProfessionalLocationsConfig: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [settingDefault, setSettingDefault] = useState<number | null>(null);

  const initialFormState: LocationFormData = {
    name: '',
    address: '',
    city: '',
    phone: '',
    is_active: true
  };

  const isBelleza = user?.professional_type === 'belleza';

  // Verificar usuario y cargar datos
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');

    if (!userData || loginStatus !== 'true') {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);

      if (!parsedUser.es_profesional) {
        navigate('/');
        return;
      }

      if (!parsedUser.professional_type ||
          !['salud', 'belleza'].includes(parsedUser.professional_type)) {
        parsedUser.professional_type = 'belleza';
      }

      setUser(parsedUser);
      loadLocations(parsedUser.id);
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  }, [navigate]);

  const loadLocations = async (professionalId: number) => {
    try {
      const response = await apiService.getLocations(professionalId);
      setLocations(response.locations);
    } catch (err: any) {
      setError('Error cargando sedes: ' + apiService.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSaveLocation = async (formData: LocationFormData) => {
    if (!user) return;
    setSaving(true);
    setError(null);

    try {
      if (formData.id) {
        await apiService.updateLocation(user.id, formData.id, {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          phone: formData.phone,
          is_active: formData.is_active
        });
        showSuccess('Sede actualizada correctamente');
      } else {
        await apiService.createLocation(user.id, {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          phone: formData.phone
        });
        showSuccess('Sede creada correctamente');
      }

      setShowForm(false);
      setEditingLocation(null);
      await loadLocations(user.id);
    } catch (err: any) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId: number) => {
    if (!user) return;
    setError(null);

    try {
      await apiService.deleteLocation(user.id, locationId);
      setDeleteConfirm(null);
      showSuccess('Sede eliminada correctamente');
      await loadLocations(user.id);
    } catch (err: any) {
      setDeleteConfirm(null);
      setError(apiService.getErrorMessage(err));
    }
  };

  const handleSetDefault = async (locationId: number) => {
    if (!user) return;
    setSettingDefault(locationId);
    setError(null);

    try {
      await apiService.setDefaultLocation(user.id, locationId);
      showSuccess('Sede principal actualizada');
      await loadLocations(user.id);
    } catch (err: any) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setSettingDefault(null);
    }
  };

  const handleEdit = (location: Location) => {
    setEditingLocation({
      id: location.id,
      name: location.name,
      address: location.address,
      city: location.city,
      phone: location.phone,
      is_active: location.is_active
    });
    setShowForm(true);
  };

  const handleNewLocation = () => {
    setEditingLocation(null);
    setShowForm(true);
  };

  // ─────────────────────────────────────────────
  // MODAL DE FORMULARIO
  // ─────────────────────────────────────────────
  const LocationFormModal = () => {
    const [formData, setFormData] = useState<LocationFormData>(
      editingLocation || { ...initialFormState }
    );
    const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});

    const updatePosition = useCallback(() => {
      setOverlayStyle({
        position: 'absolute' as const,
        top: window.scrollY,
        left: 0,
        width: '100%',
        height: window.innerHeight,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      });
    }, []);

    useEffect(() => {
      updatePosition();
      document.body.style.overflow = 'hidden';
      window.addEventListener('resize', updatePosition);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('resize', updatePosition);
      };
    }, [updatePosition]);

    const updateField = (field: string, value: any) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!formData.name.trim()) return;
      handleSaveLocation(formData);
    };

    const accentColor = isBelleza ? 'pink' : 'indigo';

    return createPortal(
      <div style={overlayStyle}>
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => { setShowForm(false); setEditingLocation(null); }}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className={`sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r ${isBelleza ? 'from-pink-50 to-purple-50' : 'from-indigo-50 to-blue-50'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-${accentColor}-100 flex items-center justify-center`}>
                <MapPin className={`w-5 h-5 text-${accentColor}-600`} />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">
                  {editingLocation?.id ? 'Editar Sede' : 'Nueva Sede'}
                </h3>
                <p className="text-xs text-gray-500">
                  {editingLocation?.id ? 'Modificar datos del consultorio' : 'Agregar un nuevo consultorio'}
                </p>
              </div>
            </div>
            <button
              onClick={() => { setShowForm(false); setEditingLocation(null); }}
              className="p-2 hover:bg-white/80 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Nombre de la sede <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
                className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-${accentColor}-500 focus:border-transparent transition-all text-sm`}
                placeholder="Ej: Consultorio Central, Sucursal Norte..."
              />
            </div>

            {/* Dirección */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Dirección
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-${accentColor}-500 focus:border-transparent transition-all text-sm`}
                placeholder="Ej: Av. San Martín 1234"
              />
            </div>

            {/* Ciudad */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Ciudad
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-${accentColor}-500 focus:border-transparent transition-all text-sm`}
                placeholder="Ej: Corrientes, Resistencia..."
              />
            </div>

            {/* Teléfono / WhatsApp */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Teléfono / WhatsApp de la sede
              </label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-${accentColor}-500 focus:border-transparent transition-all text-sm`}
                placeholder="Ej: +5493794123456"
              />
              <p className="text-xs text-gray-400 mt-1">
                Este número se usará para el botón de WhatsApp en tu perfil público
              </p>
            </div>

            {/* Estado activo (solo al editar) */}
            {editingLocation?.id && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Estado</span>
                  <p className="text-xs text-gray-500">
                    {formData.is_active ? 'Visible para pacientes' : 'Oculta para pacientes'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => updateField('is_active', !formData.is_active)}
                  className="flex items-center gap-2"
                >
                  {formData.is_active ? (
                    <ToggleRight className="w-8 h-8 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-8 h-8 text-gray-400" />
                  )}
                </button>
              </div>
            )}

            {/* Botones */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingLocation(null); }}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || !formData.name.trim()}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all flex items-center justify-center gap-2 ${
                  saving || !formData.name.trim()
                    ? 'bg-gray-300 cursor-not-allowed'
                    : isBelleza
                      ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 shadow-lg shadow-pink-500/25'
                      : 'bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 shadow-lg shadow-indigo-500/25'
                }`}
              >
                {saving ? (
                  <LoadingSpinner size={16} variant="white" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body
    );
  };

  // ─────────────────────────────────────────────
  // CARD DE SEDE
  // ─────────────────────────────────────────────
  const LocationCard = ({ location }: { location: Location }) => {
    const isDefault = location.is_default;
    const isInactive = !location.is_active;
    const isDeleting = deleteConfirm === location.id;

    return (
      <div
        className={`group relative bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
          isInactive
            ? 'opacity-50 hover:opacity-75 border-gray-200'
            : isDefault
              ? `border-${isBelleza ? 'pink' : 'indigo'}-300 shadow-md shadow-${isBelleza ? 'pink' : 'indigo'}-500/10`
              : 'border-gray-200 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-500/5'
        }`}
      >
        {/* Color bar superior */}
        <div className={`h-1 ${
          isDefault
            ? isBelleza
              ? 'bg-gradient-to-r from-pink-400 to-purple-400'
              : 'bg-gradient-to-r from-indigo-400 to-blue-400'
            : isInactive
              ? 'bg-gray-300'
              : 'bg-gradient-to-r from-gray-300 to-gray-400'
        }`} />

        <div className="p-4 sm:p-5">
          {/* Header: Nombre + Badges */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-800 text-base truncate">{location.name}</h3>
                {isDefault && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    isBelleza
                      ? 'bg-pink-50 text-pink-700 border border-pink-200'
                      : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  }`}>
                    <Star className="w-3 h-3" />
                    Principal
                  </span>
                )}
                {isInactive && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                    <EyeOff className="w-3 h-3" />
                    Inactiva
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Detalles */}
          <div className="space-y-1.5 mb-4">
            {location.city && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate">{location.city}</span>
              </div>
            )}
            {location.address && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate">{location.address}</span>
              </div>
            )}
            {location.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span>{location.phone}</span>
              </div>
            )}
            {!location.city && !location.address && !location.phone && (
              <p className="text-sm text-gray-400 italic">Sin datos de contacto</p>
            )}
          </div>

          {/* Acciones */}
          {isDeleting ? (
            <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 flex-1">¿Eliminar esta sede?</span>
              <button
                onClick={() => handleDelete(location.id)}
                className="px-3 py-1 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
              >
                Sí
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-3 py-1 bg-white text-gray-600 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                No
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {/* Botón: Marcar como principal */}
              {!isDefault && location.is_active && (
                <button
                  onClick={() => handleSetDefault(location.id)}
                  disabled={settingDefault === location.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    settingDefault === location.id
                      ? 'bg-gray-100 text-gray-400'
                      : isBelleza
                        ? 'bg-pink-50 text-pink-600 hover:bg-pink-100 border border-pink-200'
                        : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-200'
                  }`}
                >
                  {settingDefault === location.id ? (
                    <LoadingSpinner size={14} thickness={3} />
                  ) : (
                    <Star className="w-3 h-3" />
                  )}
                  Hacer principal
                </button>
              )}

              <div className="flex-1" />

              {/* Editar */}
              <button
                onClick={() => handleEdit(location)}
                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                title="Editar sede"
              >
                <Pencil className="w-4 h-4" />
              </button>

              {/* Eliminar */}
              <button
                onClick={() => setDeleteConfirm(location.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Eliminar sede"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────
  // LOADING STATE
  // ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`min-h-screen ${isBelleza ? 'bg-gradient-to-br from-pink-50 via-purple-50 to-fuchsia-50' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'} flex items-center justify-center`}>
        <LoadingSpinner size={48} label="Cargando sedes..." />
      </div>
    );
  }

  const activeLocations = locations.filter(l => l.is_active);
  const inactiveLocations = locations.filter(l => !l.is_active);

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <div className={`min-h-screen ${isBelleza ? 'bg-gradient-to-br from-pink-50 via-purple-50 to-fuchsia-50' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'}`}>
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-right fade-in duration-300">
          <div className="flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg shadow-green-500/25">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between min-h-[56px] sm:h-16 py-2 sm:py-0">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className={`hidden sm:flex w-10 h-10 rounded-xl ${isBelleza ? 'bg-pink-100' : 'bg-indigo-100'} items-center justify-center`}>
                <MapPin className={`w-5 h-5 ${isBelleza ? 'text-pink-600' : 'text-indigo-600'}`} />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-800 truncate">
                  Mis Sedes
                </h1>
                {user && (
                  <p className="text-xs text-gray-500 truncate hidden sm:block">
                    {user.nombre} {user.apellido} · {user.especialidad || 'Profesional'}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleNewLocation}
              className={`flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl text-white text-sm font-medium transition-all ${
                isBelleza
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 shadow-lg shadow-pink-500/25'
                  : 'bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 shadow-lg shadow-indigo-500/25'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva Sede</span>
              <span className="sm:hidden">Agregar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700 flex-1">{error}</span>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded-lg">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </div>
      )}

      {/* Info Banner */}
      {locations.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <div className={`flex items-start gap-3 p-4 rounded-xl border ${
            isBelleza
              ? 'bg-pink-50/50 border-pink-200/50'
              : 'bg-indigo-50/50 border-indigo-200/50'
          }`}>
            <MapPin className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isBelleza ? 'text-pink-500' : 'text-indigo-500'}`} />
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-0.5">Multi-sede habilitada</p>
              <p>Cada sede puede tener sus propios horarios. La sede marcada como <strong>principal</strong> es la que se muestra por defecto en tu perfil público.</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {locations.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className={`w-20 h-20 ${isBelleza ? 'bg-pink-100' : 'bg-indigo-100'} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
              <MapPin className={`w-10 h-10 ${isBelleza ? 'text-pink-400' : 'text-indigo-400'}`} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No tenés sedes configuradas</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Agregá tu primer consultorio o lugar de atención para empezar a gestionar tus sedes.
            </p>
            <button
              onClick={handleNewLocation}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-all ${
                isBelleza
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 shadow-lg shadow-pink-500/25'
                  : 'bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 shadow-lg shadow-indigo-500/25'
              }`}
            >
              <Plus className="w-5 h-5" />
              Agregar primera sede
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Locations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeLocations.map(location => (
                <LocationCard key={location.id} location={location} />
              ))}
            </div>

            {/* Inactive Locations */}
            {inactiveLocations.length > 0 && (
              <div className="pt-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px bg-gray-200 flex-1" />
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Sedes inactivas ({inactiveLocations.length})
                  </span>
                  <div className="h-px bg-gray-200 flex-1" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {inactiveLocations.map(location => (
                    <LocationCard key={location.id} location={location} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && <LocationFormModal />}
    </div>
  );
};

export default ProfessionalLocationsConfig;
